import { BrowserRouter, Routes, Route } from 'react-router-dom';
import Layout from './components/Layout';
import Erstinformation from './pages/Erstinformation';
import Datenschutz from './pages/Datenschutz';
import TarifBerechnen from './pages/TarifBerechnen';
import PersoenlicheAngaben from './pages/PersoenlicheAngaben';
import EMailBestaetigung from './pages/EMailBestaetigung';
import DeineZahlungsdaten from './pages/DeineZahlungsdaten';
import Zusammenfassung from './pages/Zusammenfassung';
import Danke from './pages/Danke';
import { AppProvider } from './context/AppContext';
import { GlobalErrorProvider } from './context/GlobalErrorContext';
import { CookieConsentProvider } from './context/CookieConsentContext';
import ScrollToTopOnRouteChange from './components/ScrollToTopOnRouteChange';
import CookieBanner from './components/CookieBanner';
import GuardedRoute from './components/GuardedRoute';
import { useCallback, useEffect, useState } from 'react';
import { configLoader } from './config/configLoader';
import { configService } from './backend/configService';
import Loading from './components/Loading';
import Error from './components/Error';

function App() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [globalConfig, setGlobalConfig] = useState({});
  const [configuration, setConfiguration] = useState({ products: [] });

  const loadGlobalConfig = useCallback(async () => {
    try {
      const loadedConfig = await configLoader.loadConfig();
      setGlobalConfig(loadedConfig);
    } catch (error) {
      setError(error);
    }
  }, []);

  const loadConfigurationFromApi = useCallback(async () => {
    const response = await configService.loadConfiguration();

    if (response.success) {
      setConfiguration(response.data);
    } else {
      setError('Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut');
    }
  }, []);

  // Matomo Initialisierung (einmalig)
  useEffect(() => {
    if (!loading && globalConfig) {
      window._paq = window._paq || [];
      // WICHTIG: Cookie-Consent ZUERST erfordern, bevor andere Tracking-Befehle
      window._paq.push(['requireCookieConsent']);

      var _mtm = (window._mtm = window._mtm || []);
      _mtm.push({ 'mtm.startTime': new Date().getTime(), event: 'mtm.Start' });

      (function () {
        var d = document,
          g = d.createElement('script'),
          s = d.getElementsByTagName('script')[0];
        g.async = true;
        g.src = globalConfig.MATOMO_URL;
        s.parentNode.insertBefore(g, s);
      })();
    }
  }, [globalConfig, loading]);

  useEffect(() => {
    (async () => {
      await loadGlobalConfig();
      await loadConfigurationFromApi();
      setLoading(false);
    })();
  }, [loadGlobalConfig, loadConfigurationFromApi]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <Error error={error} />;
  }

  const BASE_PATH = window.BASE_PATH && window.BASE_PATH !== '#BASE_PATH#' ? window.BASE_PATH : '/';

  return (
    <AppProvider globalConfig={globalConfig} configuration={configuration}>
      <GlobalErrorProvider>
        <CookieConsentProvider>
          <BrowserRouter basename={BASE_PATH}>
            <ScrollToTopOnRouteChange />
            <CookieBanner />
            <Routes>
              <Route
                path="/"
                element={
                  <Layout infobox={false} pageTitle="Erstinformationen bestätigen - R+V Hardwareversicherung">
                    <Erstinformation />
                  </Layout>
                }
              />
              <Route
                path="/erstinformation"
                element={
                  <Layout infobox={false} pageTitle="Erstinformationen bestätigen - R+V Hardwareversicherung">
                    <Erstinformation />
                  </Layout>
                }
              />
              <Route
                path="/tarif-berechnen"
                element={
                  <GuardedRoute minStep={1} maxStep={2}>
                    <Layout infobox={true} pageTitle="Tarif berechnen - R+V Hardwareversicherung">
                      <TarifBerechnen />
                    </Layout>
                  </GuardedRoute>
                }
              />
              <Route
                path="/persoenliche-angaben"
                element={
                  <GuardedRoute minStep={2} maxStep={2}>
                    <Layout infobox={true} pageTitle="Persönliche Angaben - R+V Hardwareversicherung">
                      <PersoenlicheAngaben />
                    </Layout>
                  </GuardedRoute>
                }
              />
              <Route
                path="/e-mail-bestaetigung"
                element={
                  <GuardedRoute minStep={3} maxStep={3} allowDeeplink={true}>
                    <Layout
                      infobox={true}
                      pageTitle="Bestätigung der E-Mail-Adresse - R+V Hardwareversicherung"
                      mainContent="doi-code-field"
                    >
                      <EMailBestaetigung />
                    </Layout>
                  </GuardedRoute>
                }
              />
              <Route
                path="/deine-zahlungsdaten"
                element={
                  <GuardedRoute minStep={4} maxStep={4}>
                    <Layout infobox={true} pageTitle="Zahlungsdaten - R+V Hardwareversicherung">
                      <DeineZahlungsdaten />
                    </Layout>
                  </GuardedRoute>
                }
              />
              <Route
                path="/zusammenfassung"
                element={
                  <GuardedRoute minStep={5} maxStep={5}>
                    <Layout infobox={false} pageTitle="Zusammenfassung - R+V Hardwareversicherung">
                      <Zusammenfassung />
                    </Layout>
                  </GuardedRoute>
                }
              />
              <Route
                path="/danke"
                element={
                  <GuardedRoute minStep={6} maxStep={6}>
                    <Layout infobox={false} pageTitle="Eingangsbestätigung - R+V Hardwareversicherung">
                      <Danke />
                    </Layout>
                  </GuardedRoute>
                }
              />
              <Route
                path="/datenschutz"
                element={
                  <Layout infobox={false} pageTitle="Datenschutz - R+V Hardwareversicherung">
                    <Datenschutz />
                  </Layout>
                }
              />
            </Routes>
          </BrowserRouter>
        </CookieConsentProvider>
      </GlobalErrorProvider>
    </AppProvider>
  );
}

export default App;
